import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export default function SystemRegister() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const { signUp } = useAuth();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!agreedToTerms) {
      return setError('You must agree to our policies to create an account');
    }

    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    try {
      setError('');
      setLoading(true);
      await signUp(email, password);
      setIsRegistered(true);
    } catch (err: any) {
      if (err.message === 'email rate limit exceeded') {
        setError('Too many registration attempts. Please try again in a few minutes.');
      } else {
        setError('Failed to create an account');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  if (isRegistered) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <Link to="/">
            <img
              src="/HRO-Logo-transparent-white-logo.png"
              alt="Horse Racing Oracle"
              className="mx-auto h-12 w-auto"
            />
          </Link>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <div className="mb-6">
              <div className="flex justify-center">
                <div className="rounded-full bg-green-100 p-3">
                  <svg className="h-12 w-12 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Registration Successful!</h2>
              <p className="mt-4 text-lg text-gray-600">
                Thank you for registering with Horse Racing Oracle.
              </p>
              <p className="mt-2 text-gray-600">
                We've sent a confirmation email to:
              </p>
              <p className="mt-1 text-lg font-medium text-gray-900">
                {email}
              </p>
            </div>

            <div className="mt-6 bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">What's Next?</h3>
              <ol className="space-y-3 text-center">
                <li className="flex items-center justify-center">
                  <span className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-primary-100 text-primary-600 font-semibold text-sm mr-3">
                    1
                  </span>
                  <span className="text-gray-600">Check your email</span>
                </li>
                <li className="flex items-center justify-center">
                  <span className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-primary-100 text-primary-600 font-semibold text-sm mr-3">
                    2
                  </span>
                  <span className="text-gray-600">Click the confirmation link</span>
                </li>
                <li className="flex items-center justify-center">
                  <span className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-primary-100 text-primary-600 font-semibold text-sm mr-3">
                    3
                  </span>
                  <span className="text-gray-600">Log in to your account</span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center">
          <img
            src="/HRO-Logo-transparent-white-logo.png"
            alt="Horse Racing Oracle"
            className="h-16"
          />
        </Link>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Or{' '}
          <Link to="/system/login" className="font-medium text-primary-600 hover:text-primary-500">
            sign in to your account
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="label">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="confirm-password" className="label">
                Confirm Password
              </label>
              <div className="mt-1">
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input"
                />
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="terms" className="text-gray-600">
                  Confirm that you agree to our{' '}
                  <Link to="/system/terms-conditions" target="_blank" className="text-primary-600 hover:text-primary-500">
                    Terms & Conditions
                  </Link>
                  ,{' '}
                  <Link to="/system/privacy-policy" target="_blank" className="text-primary-600 hover:text-primary-500">
                    Privacy Policy
                  </Link>
                  , and{' '}
                  <Link to="/system/refund-policy" target="_blank" className="text-primary-600 hover:text-primary-500">
                    Refund Policy
                  </Link>
                </label>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || !agreedToTerms}
                className={`w-full btn ${
                  !agreedToTerms 
                    ? 'bg-gray-300 cursor-not-allowed' 
                    : 'btn-primary'
                }`}
              >
                {loading ? 'Creating account...' : 'Create account'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 